"use client";

import React from "react";
import { ArrowRight, Info as HelpCircle } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const faqItems = [{
  id: "item-1",
  question: "How does Generative Engine Optimization (GEO) differ from traditional SEO?",
  answer: "Traditional SEO focuses on ranking websites in standard search engine result pages. GEO optimizes your business content so generative AI models like ChatGPT, Claude, and Perplexity synthesize and recommend your business when users ask conversational questions."
}, {
  id: "item-2",
  question: "How soon can we expect to appear in AI search recommendations?",
  answer: "Initial indexing updates usually show effect within 14 to 30 days after structured data cleanup, LLM entity indexing, and citation verification are deployed."
}, {
  id: "item-3",
  question: "Why is a website cleanup required before AI citation building?",
  answer: "AI engines parse clean, machine-readable structured schema and unambiguous service descriptions. If your website contains conflicting details or outdated markup, AI systems skip your profile in favor of clearer competitors."
}, {
  id: "item-4",
  question: "What is included in the Free AI Visibility Checkup?",
  answer: "We run custom prompts across ChatGPT, Perplexity, Gemini, and Claude for your local service area. You get a direct report showing whether AI recommends you, who it recommends instead, and exact steps to fix gaps."
}, {
  id: "item-5",
  question: "Do you offer monthly tracking and LLM reporting?",
  answer: "Yes. Every month we track your brand citations across major AI engines, monitor prompt rankings for high-intent queries, and send a clear, non-technical summary of your market visibility."
}];
export default function Questions() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return <section id="geoexplainerquestions" data-nav="dark" className="relative overflow-hidden bg-background text-foreground py-24 md:py-32 border-t border-border">
      {}
      <div className="pointer-events-none absolute right-0 top-12 text-[18rem] md:text-[28rem] font-serif font-bold leading-none text-foreground/[0.03] select-none z-0">
        03
      </div>

      <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-5 lg:sticky lg:top-28 space-y-6">
            <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-muted-foreground">
              <span className="inline-block size-2 bg-[var(--accent-deep-red)]" />
              Technical FAQ
            </div>

            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold text-foreground leading-tight">
              Frequently asked technical questions
            </h2>

            <p className="text-muted-foreground text-base leading-relaxed">
              Everything you need to know about Generative Engine Optimization, citation architecture, and structured AI indexing.
            </p>

            <div className="pt-2">
              <Link to="/ContactPage" className="inline-flex items-center gap-2 text-sm font-medium text-foreground underline-offset-4 hover:underline">
                <span>Have a specific technical question? Contact us</span>
                <ArrowRight className="size-4 text-[var(--accent-deep-red)]" />
              </Link>
            </div>
          </motion.div>

          {}
          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqItems.map((item, i) => <AccordionItem key={item.id} value={item.id} data-index={i} className="border-b border-border transition-all duration-200 data-[state=open]:border-l-2 data-[state=open]:border-l-[var(--accent-deep-red)] data-[state=open]:pl-4">
                  <AccordionTrigger className="py-6 text-left font-serif text-lg md:text-xl font-medium text-foreground hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-6 text-muted-foreground text-base leading-relaxed">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </motion.div>
        </div>

        {}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="mt-20 md:mt-28">
          <div className="border border-[var(--accent-deep-red)]/60 bg-card/60 p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8 shadow-sm">
            <div className="space-y-2 max-w-2xl">
              <div className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[var(--accent-deep-red)]">
                <HelpCircle className="size-4" />
                <span>Ready to start?</span>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl font-semibold text-card-foreground">
                Ensure AI models recommend your business today
              </h3>
              <p className="text-muted-foreground text-sm md:text-base">
                Book a direct strategy call or request your free visibility checkup report.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full md:w-auto shrink-0">
              <Button asChild size="lg" className="bg-[var(--accent-deep-red)] text-white hover:bg-[var(--accent-deep-red)]/90 font-medium">
                <Link to="/PricingPlansPage">Get started </Link>
              </Button>
              <Link className="inline-flex items-center justify-center gap-2 text-sm font-medium text-foreground hover:text-[var(--accent-deep-red)] transition-colors py-2 px-4" to="/ContactPage">
                <span>Get a free AI visibility checkup</span>
                <ArrowRight className="size-4" />
              </Link>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}